/**
 * Custom hook for managing context menu in DrivePanel
 *
 * Extracted from DrivePanel.tsx
 */

import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import type { UseDriveContextMenuProps, UseDriveContextMenuReturn } from '../types/drive-panel.types';

/**
 * Manages context menu state and positioning
 */
export function useDriveContextMenu({
}: UseDriveContextMenuProps = {}): UseDriveContextMenuReturn {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<Record<string, { top?: number; bottom?: number; left: number }>>({});
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Calcular posición del menú
  useLayoutEffect(() => {
    if (openMenuId) {
      const buttonElement = menuButtonRefs.current[openMenuId];

      if (buttonElement) {
        const buttonRect = buttonElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const menuHeight = 120; // Altura estimada del menú
        const menuWidth = 160;
        const margin = 8;

        const spaceBelow = viewportHeight - buttonRect.bottom;
        const showUp = spaceBelow < menuHeight + margin;

        let left = buttonRect.right - menuWidth;
        if (left < margin) left = margin;

        if (showUp) {
          setMenuPosition(prev => ({
            ...prev,
            [openMenuId]: {
              bottom: viewportHeight - buttonRect.top + margin,
              left: left,
            },
          }));
        } else {
          setMenuPosition(prev => ({
            ...prev,
            [openMenuId]: {
              top: buttonRect.bottom + margin,
              left: left,
            },
          }));
        }
      }
    }
  }, [openMenuId]);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    if (!openMenuId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const menuElement = menuRefs.current[openMenuId];
      const buttonElement = menuButtonRefs.current[openMenuId];

      if (
        menuElement && !menuElement.contains(event.target as Node) &&
        buttonElement && !buttonElement.contains(event.target as Node)
      ) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  return {
    openMenuId,
    menuPosition,
    menuButtonRefs,
    menuRefs,
    setOpenMenuId,
  };
}
